<template>
    <div :class="getRootClass()" ref="root">
        <h2 class="title mb-2">タスク一覧</h2>
        <!-- データ取得中の表示 -->
        <div v-if="loading">
            <ul id="task-list">
                <li
                    v-for="n in 5"
                    :key="n"
                    class="task-item mb-2 blank-item"
                ></li>
            </ul>
        </div>
        <div v-else>
            <!-- タスクがなかった時の表示 -->
            <div v-if="success">
                <div v-if="tasks.length === 0">
                    <div class="py-2 px-2 mb-2 text-info">
                        タスクはありません
                    </div>
                </div>

                <!-- タスクがある場合の表示 -->
                <div v-else>
                    <ul id="task-list">
                        <li
                            v-for="task in tasks"
                            :key="task.id"
                            class="task-item"
                        >
                            <a
                                :href="task.link"
                                class="d-flex flex-row align-items-center border mb-2 js-anchor-click"
                            >
                                <div class="d-flex justify-content-center w5">
                                    <i
                                        class="fas fa-check-circle font-lg text-primary"
                                    ></i>
                                </div>
                                <div
                                    :class="
                                        task.limit ? 'w75 border-right' : ''
                                    "
                                >
                                    <div
                                        class="d-flex justify-content-between py-2 px-2"
                                    >
                                        <!-- タスク内容 -->
                                        <div class="">
                                            <h3 class="color-body font-xs">
                                                {{ task.content }}
                                            </h3>
                                            <div class="text-info font-xs">
                                                {{ task.title }} {{ task.name }}
                                            </div>
                                        </div>

                                        <!-- アラート -->
                                        <div
                                            v-if="task.alerts.length > 0"
                                            class="alert-box text-danger font-xxs w20"
                                        >
                                            <div v-if="task.alerts.length > 2">
                                                <i
                                                    class="fas fa-exclamation-triangle"
                                                ></i
                                                >{{ task.alerts[0].content
                                                }}<br />
                                                その他{{
                                                    task.alerts.length - 1
                                                }}件<br />
                                            </div>
                                            <div v-else>
                                                <div
                                                    v-for="alert in task.alerts"
                                                    :key="alert.content"
                                                >
                                                    <i
                                                        class="fas fa-exclamation-triangle"
                                                    ></i
                                                    >{{ alert.content }}<br />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="task.limit"
                                    class="w20 d-flex justify-content-center text-center"
                                >
                                    <span v-html="task.limit"></span>
                                </div>
                            </a>
                            <div class="btn-group dropright task-option">
                                <i
                                    class="fas fa-cog"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                ></i>
                                <div class="dropdown-menu">
                                    <span
                                        class="dropdown-item"
                                        @click="deleteTask(task.id)"
                                        >タスクを消去</span
                                    >
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>

            <!-- データ取得中にエラーが発生した場合 -->
            <div v-else>
                <div class="py-2 px-2 mb-2 text-danger">
                    エラーによりタスクが表示できません。<br />
                    改善しない場合はお手数をおかけしますが、{{
                        supportEmail
                    }}までお問い合わせください。
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.task-list-component > div {
    max-height: 100%;
    overflow-y: auto;
}
.blank-item {
    width: 100%;
    height: 3.6rem;
    background: #e9f1f2;
}
.alert-box {
    display: flex;
    flex-flow: column;
    justify-content: center;
}
.task-list-component {
    padding: 0;
    height: 299px;
}
.task-list-component.short {
    height: 239px;
}
.title {
    display: block;
    padding: 0;
    height: 34px;
}
</style>

<script>
import { ref, onMounted } from "vue";

export default {
    props: ["supportEmail"],
    setup(_props) {
        const tasks = ref([]);
        const loading = ref(true);
        const success = ref(false);

        const actor = () => {
            const currentPath = window.location.pathname;
            if (currentPath.includes("/client/")) {
                return "client";
            } else if (currentPath.includes("/agent/")) {
                return "agent";
            } else if (currentPath.includes("/partner/")) {
                return "partner";
            }
        };

        const createEndpoint = () => {
            return `/${actor()}/api/task/getAll`;
        };

        const getRootClass = () => {
            return {
                "task-list-component": true,
                short: actor() !== "client",
            };
        };

        const deleteTask = (task_id) => {
            const tempTasks = tasks.value;
            tasks.value = tasks.value.filter((task) => task.id !== task_id);

            fetch(`/${actor()}/top/task`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-TOKEN": document.querySelector(
                        'meta[name="csrf-token"]'
                    ).content,
                },
                credentials: "include",
                body: JSON.stringify({
                    task_id: task_id,
                }),
            }).then((response) => {
                if (!response.ok) {
                    tasks.value = tempTasks;
                    throw new Error("Network response was not ok");
                }
            });
        };

        onMounted(() => {
            const endpoint = createEndpoint();

            fetch(endpoint, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error("Network response was not ok");
                    }
                    return response.json();
                })
                .then((data) => {
                    tasks.value = data ? data : [];
                    success.value = true;
                    loading.value = false;
                })
                .catch((_error) => {
                    loading.value = false;
                });
        });

        return {
            tasks,
            loading,
            success,
            getRootClass,
            deleteTask,
        };
    },
};
</script>
